import React, {
	ChangeEvent,
	FC,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router-dom";

import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { AlertToast } from "@/helpers/alerts/AlertToast";
import { FormAttachment } from "@/helpers/components/FormAttachment";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { SuggestionRequest } from "@/model/Procedure/ProcedureNodeStepComment";
import { UpdateStepStatusRequest } from "@/model/Procedure/UpdateStepStatusRequest";
import { UserContext } from "@/model/User/UserContext";

import {
	PostProcedureUpdateSuggestions,
	useUpdateStepStatus,
} from "@/service/ProcedureMutations";
import { useFetchActiveProcedure } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface AddCommentProps {
	checklistId: number | undefined;
	showAddComment: boolean;
	step: ProcedureNodeStep;
	closeModal: () => void;
	skipComment: boolean;
	skipStep: () => Promise<void>;
}

type Mode = "suggestion" | "comment";

const MAX_COMMENT_CHARS = 2000;

export const AddComment: FC<AddCommentProps> = (props) => {
	const theme = useTheme();
	const { id } = useParams<RouteParams>();
	const username = useContext(UserContext)?.Name;
	const { timezone } = useTime();

	// form
	const [mode, setMode] = useState<Mode>("comment");
	const [comment, setComment] = useState("");
	const [file, setFile] = useState<File | undefined>();
	const [fileName, setFileName] = useState<string | undefined>();
	const [taskDate] = useState<Date>(new Date());
	const [selectedTaskGroup, setSelectedTaskGroup] = useState<number>(-1);

	// camera
	const [cameraOpen, setCameraOpen] = useState(false);
	const [cameraLoading, setCameraLoading] = useState(false);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	// data
	const { data: procedure } = useFetchActiveProcedure();

	// api
	const updateStepState = useUpdateStepStatus();
	const createProcedureUpdateSuggestion = PostProcedureUpdateSuggestions();

	const dialogTitle = useMemo(
		() =>
			props.skipComment ? (
				<Translate
					String="Procedure.CommentOverlay.AddSkipComment"
					HTMLTag="span"
				>
					Add skip comment
				</Translate>
			) : (
				<Translate String="Procedure.CommentOverlay.AddComment" HTMLTag="span">
					Add comment
				</Translate>
			),
		[props.skipComment],
	);

	const setPickedFile = (f?: File) => {
		setFile(f);
		setFileName(f?.name || undefined);
	};

	/** Camera helpers */
	const stopCamera = () => {
		const s = streamRef.current;
		if (s) {
			s.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		const v = videoRef.current;
		if (v) {
			try {
				(v as any).srcObject = null;
			} catch {
				v.removeAttribute("src");
			}
		}
		setCameraOpen(false);
		setCameraLoading(false);
	};

	const tryGetStream = async (constraints: MediaStreamConstraints) =>
		navigator.mediaDevices.getUserMedia(constraints);

	const attachAndPlay = async (stream: MediaStream) => {
		const v = videoRef.current!;
		try {
			(v as any).srcObject = stream;
		} catch {
			v.srcObject = null as any;
			// @ts-ignore
			v.src = URL.createObjectURL(stream);
		}
		v.setAttribute("muted", "true");
		v.setAttribute("playsinline", "true");
		v.setAttribute("autoplay", "true");
		v.muted = true;

		if (v.readyState < 2) {
			await new Promise<void>((resolve) => {
				const onLoaded = () => {
					v.removeEventListener("loadedmetadata", onLoaded);
					resolve();
				};
				v.addEventListener("loadedmetadata", onLoaded, { once: true });
			});
		}
		try {
			await v.play();
		} catch (e) {
			console.warn("video.play() was blocked:", e);
		}
	};

	const startCamera = async () => {
		setCameraError(null);
		setCameraLoading(true);

		const attempts: MediaStreamConstraints[] = [
			{
				video: {
					facingMode: { ideal: "environment" },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			},
			{
				video: {
					facingMode: { ideal: "user" },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			},
			{ video: true, audio: false },
		];

		for (let i = 0; i < attempts.length; i++) {
			try {
				const stream = await tryGetStream(attempts[i]);
				streamRef.current = stream;
				setCameraOpen(true);
				await attachAndPlay(stream);

				// watchdog — ensure we actually got frames
				const ok = await new Promise<boolean>((resolve) => {
					setTimeout(() => {
						const v = videoRef.current!;
						resolve(Boolean(v.videoWidth && v.videoHeight));
					}, 1500);
				});

				if (ok) {
					setCameraLoading(false);
					return;
				} else {
					stopCamera();
				}
			} catch (err) {
				stopCamera();
				if (i === attempts.length - 1) {
					console.error(err);
				}
			}
		}
		setCameraError(
			"Could not start the camera. Check permissions and try again.",
		);
		setCameraLoading(false);
	};

	const captureFrame = () => {
		if (!videoRef.current || !canvasRef.current) return;
		const v = videoRef.current;
		const c = canvasRef.current;
		const ctx = c.getContext("2d");
		if (!ctx) return;

		const vw = v.videoWidth || 640;
		const vh = v.videoHeight || 360;
		c.width = vw;
		c.height = vh;
		ctx.drawImage(v, 0, 0, vw, vh);
		c.toBlob((blob) => {
			if (!blob) return;
			const captured = new File([blob], "captured_image.png", {
				type: "image/png",
			});
			setPickedFile(captured);
			AlertToast.success("Photo captured");
		}, "image/png");
		stopCamera();
	};

	/** Files */
	const onFilePick = (e: ChangeEvent<HTMLInputElement>) => {
		const f = e?.target?.files?.[0];
		if (!f) return;
		setPickedFile(f);
	};

	const removeFile = () => setPickedFile(undefined);
	const openFilePicker = () => fileInputRef.current?.click();

	const onDrop = (e: React.DragEvent) => {
		e.preventDefault();
		const f = e.dataTransfer.files?.[0];
		if (!f) return;
		setPickedFile(f);
	};

	/** Save */
	const handleSave = async () => {
		try {
			if (!props.checklistId) {
				AlertRequest.failed("Active scheme ID is required.");
				return;
			}
			if (!id) {
				AlertRequest.failed("Procedure ID is missing.");
				return;
			}
			const trimmed = comment.trim();
			if (!trimmed) {
				AlertRequest.failed("Comment cannot be empty.");
				return;
			}
			if (trimmed.length > MAX_COMMENT_CHARS) {
				AlertRequest.failed("Comment is too long.");
				return;
			}

			if (mode === "comment") {
				const request: UpdateStepStatusRequest = {
					ArchiveType: "Comment",
					File: file,
					FileName: fileName,
					ProcedureId: parseInt(id, 10),
					SchemeId: props.checklistId,
					Status: props.step.Status,
					StepId: props.step.Id,
					TaskDate: dayjs(taskDate).tz(timezone).toISOString(),
					TaskGroup: selectedTaskGroup,
					Value: trimmed,
					UserName: username,
				};
				await updateStepState(request);
			} else {
				const req: SuggestionRequest = {
					File: file,
					ProcedureId: parseInt(id, 10),
					StepId: props.step.Id,
					ChecklistId: props.checklistId!,
					Description: trimmed,
					Anonymous: false,
				};
				await createProcedureUpdateSuggestion(req);
			}

			if (props.skipComment) {
				await props.skipStep();
			}

			AlertToast.success();
			closeDialog();
		} catch (err) {
			console.error(err);
			AlertRequest.failed();
		}
	};

	/** Lifecycle */
	const resetForm = () => {
		setComment("");
		setPickedFile(undefined);
		setSelectedTaskGroup(-1);
		setMode(props.skipComment ? "comment" : "suggestion");
	};

	const closeDialog = () => {
		stopCamera();
		resetForm();
		props.closeModal();
	};

	// ESC to close
	useEffect(() => {
		const onEsc = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				closeDialog();
			}
		};
		window.addEventListener("keydown", onEsc);
		return () => window.removeEventListener("keydown", onEsc);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// default mode & task group when opened
	useEffect(() => {
		if (!props.showAddComment) return;

		const updatesGroupId = procedure?.TaskGroups?.find(
			(g) => g.Name === "Procedure updates",
		)?.TaskGroupId;

		setSelectedTaskGroup(
			props.skipComment ? -1 : ((updatesGroupId as number) ?? -1),
		);
		setMode(props.skipComment ? "comment" : "suggestion");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.showAddComment, procedure, props.skipComment]);

	// cleanup camera on unmount
	useEffect(() => {
		return () => stopCamera();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isSaveDisabled =
		!comment.trim() || comment.trim().length > MAX_COMMENT_CHARS;

	return (
		<Dialog
			open={props.showAddComment}
			onClose={closeDialog}
			fullWidth
			maxWidth="sm"
			aria-labelledby="add-comment-title"
		>
			<DialogTitle id="add-comment-title" sx={{ pr: 6 }}>
				{dialogTitle}
				<IconButton
					aria-label="close"
					onClick={closeDialog}
					sx={{ position: "absolute", right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2}>
					{/* Mode selector */}
					<ToggleButtonGroup
						exclusive
						size="small"
						color="info"
						value={mode}
						onChange={(_, v: Mode | null) => {
							if (!v) return;
							setMode(v);
						}}
						fullWidth
						disabled={props.skipComment}
					>
						<ToggleButton value="suggestion">
							<Translate String="Procedure.Checklist.Step.Suggestion.Suggestion">
								Improvement
							</Translate>
						</ToggleButton>
						<ToggleButton value="comment">
							<Translate String="Procedure.Checklist.Step.Comment.Comment">
								Comment
							</Translate>
						</ToggleButton>
					</ToggleButtonGroup>

					{/* Helper text */}
					{!comment && (
						<Typography variant="body2" color="text.secondary">
							{mode === "suggestion" ? (
								<Translate String="Procedure.Checklist.Comment.Suggestion.Text">
									Use this to suggest improvements, fix misinformation, or
									spelling mistakes.
								</Translate>
							) : (
								<Translate String="Procedure.Checklist.Comment.Comment.Text">
									Use this to leave a note about performing this step.
								</Translate>
							)}
						</Typography>
					)}

					{/* Text area with counter */}
					<Box>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
						>
							<Translate
								htmlFor="comment"
								HTMLTag="label"
								String="Procedure.CommentOverlay.Comment"
							>
								{mode === "comment" ? "Comment" : "Write your suggestion"}
							</Translate>
							<Typography variant="caption" color="text.secondary">
								{Math.min(comment.length, MAX_COMMENT_CHARS)} /{" "}
								{MAX_COMMENT_CHARS}
							</Typography>
						</Stack>

						<TextField
							id="comment"
							name="comment"
							value={comment}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								setComment(e.target.value.slice(0, MAX_COMMENT_CHARS))
							}
							placeholder={
								mode === "comment"
									? "Write a comment about this step…"
									: "Describe your improvement suggestion…"
							}
							multiline
							minRows={4}
							fullWidth
							sx={{ mt: 1 }}
							error={!comment.trim()}
							helperText={!comment.trim() ? "Comment cannot be empty." : " "}
						/>
					</Box>

					<Divider />

					{/* Attachment section */}
					<Stack spacing={1}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
						>
							<Typography variant="subtitle2">
								<Translate String="Procedure.CommentOverlay.AddAttachment">
									Add attachment
								</Translate>
							</Typography>

							<Stack direction="row" spacing={1}>
								<input
									ref={fileInputRef}
									hidden
									type="file"
									accept="image/*"
									onChange={onFilePick}
								/>
								<Button
									sx={{ textTransform: "none", fontWeight: 800 }}
									size="small"
									variant="outlined"
									startIcon={<FileUploadIcon />}
									onClick={openFilePicker}
								>
									<Translate String="Tasks.TaskView.Comment.Choose.File">
										Choose file
									</Translate>
								</Button>
								<Button
									sx={{ textTransform: "none", fontWeight: 800 }}
									size="small"
									variant={cameraOpen ? "contained" : "outlined"}
									color="info"
									startIcon={<CameraAltIcon />}
									onClick={cameraOpen ? stopCamera : startCamera}
								>
									{cameraOpen ? (
										<Translate String="Tasks.TaskView.Comment.Capture.Image">
											Capture
										</Translate>
									) : (
										<Translate String="Tasks.TaskView.Comment.Open.Camera">
											Open Camera
										</Translate>
									)}
								</Button>
							</Stack>
						</Stack>

						{/* Drop zone */}
						<Box
							onDragOver={(e) => {
								e.preventDefault();
								e.dataTransfer.dropEffect = "copy";
							}}
							onDrop={onDrop}
							onClick={openFilePicker}
							sx={{
								mt: 1,
								p: 2,
								textAlign: "center",
								border: `1px dashed ${theme.palette.divider}`,
								borderRadius: 1,
								color: "text.secondary",
								cursor: "pointer",
								userSelect: "none",
							}}
						>
							<Translate>
								Drag & drop an image here, or click to browse
							</Translate>
						</Box>

						{/* Camera preview + actions */}
						{(cameraOpen || cameraLoading || cameraError) && (
							<Box
								sx={{
									mt: 1,
									p: 1,
									border: (t) => `1px dashed ${t.palette.divider}`,
									borderRadius: 1,
									bgcolor: "background.paper",
								}}
							>
								{cameraLoading && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ p: 2, textAlign: "center" }}
									>
										Starting camera…
									</Typography>
								)}

								{cameraError && (
									<Typography
										variant="body2"
										color="error"
										sx={{ p: 2, textAlign: "center" }}
									>
										{cameraError}
									</Typography>
								)}

								{cameraOpen && !cameraLoading && !cameraError && (
									<>
										<video
											ref={videoRef}
											autoPlay
											playsInline
											muted
											style={{
												width: "100%",
												height: 280,
												background: "#000",
												objectFit: "cover",
												borderRadius: 8,
												display: "block",
											}}
										/>
										<canvas ref={canvasRef} style={{ display: "none" }} />
										<Stack
											direction="row"
											spacing={1}
											justifyContent="flex-end"
											sx={{ mt: 1 }}
										>
											<Button
												size="small"
												color="inherit"
												onClick={stopCamera}
												sx={{ textTransform: "none", fontWeight: 800 }}
											>
												<Translate String="LanguageSettings.Table.Cancel">
													Cancel
												</Translate>
											</Button>
											<Button
												size="small"
												variant="contained"
												onClick={captureFrame}
												sx={{ textTransform: "none", fontWeight: 800 }}
											>
												<Translate String="Tasks.TaskView.Comment.Capture.Image">
													Capture Image
												</Translate>
											</Button>
										</Stack>
									</>
								)}
							</Box>
						)}

						{/* Selected file preview */}
						{fileName && (
							<FormAttachment
								id={0}
								filename={fileName}
								handleDelete={removeFile}
							/>
						)}
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button
					onClick={closeDialog}
					variant="outlined"
					color="warning"
					sx={{ textTransform: "none", fontWeight: 800 }}
				>
					<Translate String="LanguageSettings.Table.Cancel">Cancel</Translate>
				</Button>
				<Button
					sx={{ textTransform: "none", fontWeight: 800 }}
					onClick={handleSave}
					variant="contained"
					color="info"
					disabled={isSaveDisabled}
				>
					<Translate String="Tasks.Task.Comment.Save">Save</Translate>
				</Button>
			</DialogActions>
		</Dialog>
	);
};
